import React, { useState, useEffect } from 'react';
import {
  FileText,
  Music2,
  Users,
  Calendar,
  Clock,
  Globe,
  Share2,
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  FileCheck,
  UploadCloud,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import CustomSelect from './CustomSelect';
import DistributionDatePicker from './DistributionDatePicker';
import DistributionTimePicker from './DistributionTimePicker';
import {
  GENRE_OPTIONS,
  MOOD_OPTIONS,
  LANGUAGE_OPTIONS,
  VERSION_OPTIONS,
  PERFORMER_ROLES,
  COUNTRIES_LIST,
  getEarliestReleaseDate,
  isReleaseDateValid,
  validateISRC,
  validateLegalName,
  validatePLine,
  validateCLine,
  deriveReleaseYear,
  formatCLine,
  formatPLine,
} from '../../lib/distributionService';
import ValidationErrorsModal from './ValidationErrorsModal';

function InstagramIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <defs>
        <linearGradient id="distIgGrad" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#f09433" />
          <stop offset="25%" stopColor="#e6683c" />
          <stop offset="50%" stopColor="#dc2743" />
          <stop offset="75%" stopColor="#cc2366" />
          <stop offset="100%" stopColor="#bc1888" />
        </linearGradient>
      </defs>
      <rect width="24" height="24" rx="6" fill="url(#distIgGrad)" />
      <rect x="5.5" y="5.5" width="13" height="13" rx="3.5" stroke="#ffffff" strokeWidth="1.5" fill="none" />
      <circle cx="12" cy="12" r="3.2" stroke="#ffffff" strokeWidth="1.5" fill="none" />
      <circle cx="15.8" cy="8.2" r="0.9" fill="#ffffff" />
    </svg>
  );
}

function YouTubeIcon() {
  return (
    <svg width="26" height="20" viewBox="0 0 28 20" fill="none">
      <rect width="28" height="20" rx="4" fill="#ff0000" />
      <polygon points="11,5.5 19,10 11,14.5" fill="#ffffff" />
    </svg>
  );
}

function TikTokIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="5" fill="#000000" />
      <path
        d="M16 6.5c-.8-.5-1.4-1.3-1.6-2.3H12v11.2c0 1.5-1.2 2.6-2.6 2.6-1.5 0-2.6-1.2-2.6-2.6 0-1.5 1.2-2.6 2.6-2.6.3 0 .6.05.8.15V9.8c-.3-.04-.5-.06-.8-.06-3 0-5.4 2.4-5.4 5.4s2.4 5.4 5.4 5.4 5.4-2.4 5.4-5.4V9.2c1.3.9 2.9 1.4 4.6 1.4V7.8c-1.3 0-2.3-.5-2.8-1.3z"
        fill="#00f2fe"
        opacity="0.8"
      />
      <path
        d="M16.4 6.1c-.8-.5-1.4-1.3-1.6-2.3H12.4v11.2c0 1.5-1.2 2.6-2.6 2.6-1.5 0-2.6-1.2-2.6-2.6 0-1.5 1.2-2.6 2.6-2.6.3 0 .6.05.8.15V9.4c-.3-.04-.5-.06-.8-.06-3 0-5.4 2.4-5.4 5.4s2.4 5.4 5.4 5.4 5.4-2.4 5.4-5.4V8.8c1.3.9 2.9 1.4 4.6 1.4V7.4c-1.3 0-2.3-.5-2.8-1.3z"
        fill="#fe2c55"
        opacity="0.8"
      />
      <path
        d="M16.2 6.3c-.8-.5-1.4-1.3-1.6-2.3H12.2v11.2c0 1.5-1.2 2.6-2.6 2.6-1.5 0-2.6-1.2-2.6-2.6 0-1.5 1.2-2.6 2.6-2.6.3 0 .6.05.8.15V9.6c-.3-.04-.5-.06-.8-.06-3 0-5.4 2.4-5.4 5.4s2.4 5.4 5.4 5.4 5.4-2.4 5.4-5.4V9c1.3.9 2.9 1.4 4.6 1.4V7.6c-1.3 0-2.3-.5-2.8-1.3z"
        fill="#ffffff"
      />
    </svg>
  );
}

export default function Step3TrackDetails({
  releaseData,
  onUpdateReleaseData,
  audioTracks,
  profile,
  step3View = 'release-info',
  onStep3ViewChange,
  onContinue,
  onBack,
}) {
  const [selectedTrackIndex, setSelectedTrackIndex] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);

  const earliestDate = getEarliestReleaseDate();
  const totalTracks = audioTracks?.length || releaseData.tracks?.length || 1;

  // Initialize and synchronize tracks state
  useEffect(() => {
    if (!audioTracks || audioTracks.length === 0) return;
    const currentTracks = releaseData.tracks || [];
    if (currentTracks.length < audioTracks.length) {
      const merged = audioTracks.map((at, idx) => {
        if (currentTracks[idx]) return currentTracks[idx];
        return {
          title:
            releaseData.releaseType === 'single' && idx === 0 && releaseData.title
              ? releaseData.title
              : at.filename
              ? at.filename.replace(/\.[^/.]+$/, '')
              : `Track ${idx + 1}`,
          versionType: 'Original',
          genre: releaseData.genre || 'Pop',
          mood: releaseData.mood || 'Happy / Joyful',
          language: 'Hindi',
          isrc: '',
          explicit: false,
          vocalType: 'Vocal',
          countryRecorded: 'India',
          recordingYear: new Date().getFullYear(),
          hasLyrics: false,
          lyrics: '',
          composers: releaseData.legalName ? [releaseData.legalName] : [''],
          lyricists: releaseData.legalName ? [releaseData.legalName] : [''],
          mixEngineer: '',
          masterEngineer: '',
          performers: [{ name: '', role: 'Vocalist' }],
          hasSamples: false,
          samples: [{ url: '', licenseUrl: '' }],
          aiUsed: false,
          aiDeclarationFile: null,
          aiDeclarationFileName: null,
        };
      });
      onUpdateReleaseData((prev) => ({ ...prev, tracks: merged }));
    }
  }, [audioTracks, releaseData.tracks, releaseData.title, releaseData.legalName, releaseData.genre, releaseData.mood, releaseData.releaseType, onUpdateReleaseData]);

  /* -------------------------------------------------------------
     Release-Level Updaters (For Release Information page)
     ------------------------------------------------------------- */
  const handleReleaseChange = (field, value) => {
    onUpdateReleaseData((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === 'releaseDate' && value) {
        const newYear = deriveReleaseYear(value);
        if (prev.cLine) {
          const cParsed = validateCLine(prev.cLine);
          const holder = cParsed.rightsHolder || prev.cLine.replace(/^(?:©|\(C\)|C)\s*\d{4}\s*/i, '').trim();
          next.cLine = formatCLine(newYear, holder);
        }
        if (prev.pLine) {
          const pParsed = validatePLine(prev.pLine);
          const holder = pParsed.rightsHolder || prev.pLine.replace(/^(?:℗|\(P\)|P)\s*\d{4}\s*/i, '').trim();
          next.pLine = formatPLine(newYear, holder);
        }
      }

      return next;
    });
  };

  const handleSocialToggle = (platform) => {
    onUpdateReleaseData((prev) => {
      const currentVal = prev.socialMonetisation?.[platform] !== false;
      return {
        ...prev,
        socialMonetisation: {
          ...prev.socialMonetisation,
          [platform]: !currentVal,
        },
      };
    });
  };

  const handleAddPrimaryArtist = () => {
    const artists = [...(releaseData.primaryArtists || [''])];
    artists.push('');
    handleReleaseChange('primaryArtists', artists);
  };

  const handleUpdatePrimaryArtist = (index, value) => {
    const artists = [...(releaseData.primaryArtists || [''])];
    artists[index] = value;
    handleReleaseChange('primaryArtists', artists);
  };

  const handleRemovePrimaryArtist = (index) => {
    const artists = [...(releaseData.primaryArtists || [''])];
    if (artists.length <= 1) return;
    artists.splice(index, 1);
    handleReleaseChange('primaryArtists', artists);
  };

  const handleAddFeaturingArtist = () => {
    const artists = [...(releaseData.featuringArtists || [])];
    artists.push('');
    handleReleaseChange('featuringArtists', artists);
  };

  const handleUpdateFeaturingArtist = (index, value) => {
    const artists = [...(releaseData.featuringArtists || [])];
    artists[index] = value;
    handleReleaseChange('featuringArtists', artists);
  };

  const handleRemoveFeaturingArtist = (index) => {
    const artists = [...(releaseData.featuringArtists || [])];
    artists.splice(index, 1);
    handleReleaseChange('featuringArtists', artists);
  };

  /* -------------------------------------------------------------
     Track-Level Updaters (For Track Details page)
     ------------------------------------------------------------- */
  const handleTrackChange = (trackIndex, field, value) => {
    onUpdateReleaseData((prev) => {
      const tracks = [...(prev.tracks || [])];
      tracks[trackIndex] = {
        ...tracks[trackIndex],
        [field]: value,
      };
      return { ...prev, tracks };
    });
  };

  const handleAddLyricist = (trackIndex) => {
    const track = releaseData.tracks[trackIndex] || {};
    const lyricists = [...(track.lyricists || [''])];
    lyricists.push('');
    handleTrackChange(trackIndex, 'lyricists', lyricists);
  };

  const handleUpdateLyricist = (trackIndex, lIdx, value) => {
    const track = releaseData.tracks[trackIndex] || {};
    const lyricists = [...(track.lyricists || [''])];
    lyricists[lIdx] = value;
    handleTrackChange(trackIndex, 'lyricists', lyricists);
  };

  const handleRemoveLyricist = (trackIndex, lIdx) => {
    const track = releaseData.tracks[trackIndex] || {};
    const lyricists = [...(track.lyricists || [''])];
    if (lyricists.length <= 1) return;
    lyricists.splice(lIdx, 1);
    handleTrackChange(trackIndex, 'lyricists', lyricists);
  };

  const handleAddComposer = (trackIndex) => {
    const track = releaseData.tracks[trackIndex] || {};
    const composers = [...(track.composers || [''])];
    composers.push('');
    handleTrackChange(trackIndex, 'composers', composers);
  };

  const handleUpdateComposer = (trackIndex, cIdx, value) => {
    const track = releaseData.tracks[trackIndex] || {};
    const composers = [...(track.composers || [''])];
    composers[cIdx] = value;
    handleTrackChange(trackIndex, 'composers', composers);
  };

  const handleRemoveComposer = (trackIndex, cIdx) => {
    const track = releaseData.tracks[trackIndex] || {};
    const composers = [...(track.composers || [''])];
    if (composers.length <= 1) return;
    composers.splice(cIdx, 1);
    handleTrackChange(trackIndex, 'composers', composers);
  };

  const handleAddPerformer = (trackIndex) => {
    const track = releaseData.tracks[trackIndex] || {};
    const performers = [...(track.performers || [])];
    performers.push({ name: '', role: 'Vocalist' });
    handleTrackChange(trackIndex, 'performers', performers);
  };

  const handleUpdatePerformer = (trackIndex, pIdx, field, value) => {
    const track = releaseData.tracks[trackIndex] || {};
    const performers = [...(track.performers || [])];
    performers[pIdx] = {
      ...performers[pIdx],
      [field]: value,
    };
    handleTrackChange(trackIndex, 'performers', performers);
  };

  const handleRemovePerformer = (trackIndex, pIdx) => {
    const track = releaseData.tracks[trackIndex] || {};
    const performers = [...(track.performers || [])];
    if (performers.length <= 1) {
      performers[0] = { name: '', role: 'Vocalist' };
    } else {
      performers.splice(pIdx, 1);
    }
    handleTrackChange(trackIndex, 'performers', performers);
  };

  const handleAddSample = (trackIndex) => {
    const track = releaseData.tracks[trackIndex] || {};
    const samples = [...(track.samples || [{ url: '', licenseUrl: '' }])];
    samples.push({ url: '', licenseUrl: '' });
    handleTrackChange(trackIndex, 'samples', samples);
  };

  const handleUpdateSample = (trackIndex, sIdx, field, value) => {
    const track = releaseData.tracks[trackIndex] || {};
    const samples = [...(track.samples || [{ url: '', licenseUrl: '' }])];
    samples[sIdx] = {
      ...samples[sIdx],
      [field]: value,
    };
    handleTrackChange(trackIndex, 'samples', samples);
  };

  const handleRemoveSample = (trackIndex, sIdx) => {
    const track = releaseData.tracks[trackIndex] || {};
    const samples = [...(track.samples || [{ url: '', licenseUrl: '' }])];
    if (samples.length <= 1) return;
    samples.splice(sIdx, 1);
    handleTrackChange(trackIndex, 'samples', samples);
  };

  const handleAiPdfUpload = (trackIndex, file) => {
    if (!file) return;

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Strict File Rule: AI Declaration must be a PDF document (application/pdf).');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert(`Strict File Rule: PDF file size must not exceed 5 MB. Selected file is ${(file.size / (1024 * 1024)).toFixed(1)} MB.`);
      return;
    }

    handleTrackChange(trackIndex, 'aiDeclarationFile', file);
    handleTrackChange(trackIndex, 'aiDeclarationFileName', file.name);
  };

  /* -------------------------------------------------------------
     Progression 1: Release Information -> Track Details
     ------------------------------------------------------------- */
  const handleProceedToTrackDetails = () => {
    const errors = {};
    const stageName = profile?.stage_name || profile?.name || '';
    const artistIdentities = [
      stageName,
      ...(releaseData.primaryArtists || []),
      ...(releaseData.featuringArtists || []),
    ].filter(Boolean);

    if (!releaseData.title?.trim()) {
      errors.title = 'Release title is required.';
    }

    if (!releaseData.legalName?.trim()) {
      errors.legalName = 'Creator Legal Name is required.';
    } else {
      const creatorLegalCheck = validateLegalName(releaseData.legalName, stageName, artistIdentities);
      if (!creatorLegalCheck.isValid) {
        errors.legalName = creatorLegalCheck.error;
      }
    }

    if (!releaseData.genre) {
      errors.genre = 'Please select a primary genre.';
    }

    if (!releaseData.mood) {
      errors.mood = 'Please select a release mood.';
    }

    if (!releaseData.releaseDate) {
      errors.releaseDate = 'Release date is required.';
    } else if (!isReleaseDateValid(releaseData.releaseDate)) {
      errors.releaseDate = 'Release date must be at least 7 days from today.';
    }

    if (releaseData.isrc && !validateISRC(releaseData.isrc)) {
      errors.isrc = 'Invalid ISRC format (12 alphanumeric characters required).';
    }

    const primaryArtists = (releaseData.primaryArtists || []).filter((a) => a && a.trim());
    if (primaryArtists.length === 0) {
      errors.primaryArtists = 'At least 1 primary artist name is required.';
    }

    const pLineCheck = validatePLine(releaseData.pLine);
    if (!pLineCheck.isValid) {
      errors.pLine = pLineCheck.error;
    }

    const cLineCheck = validateCLine(releaseData.cLine);
    if (!cLineCheck.isValid) {
      errors.cLine = cLineCheck.error;
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setIsValidationModalOpen(true);
      return;
    }

    // Auto-sync release metadata to track 1 if empty
    const leg = releaseData.legalName?.trim() || profile?.legal_name?.trim() || '';
    const syncedTracks = (releaseData.tracks || []).map((t, idx) => {
      const trk = { ...t };
      if (!trk.title?.trim() && releaseData.title?.trim()) {
        trk.title = releaseData.title.trim();
      }
      if ((!trk.composers || trk.composers.length === 0 || !trk.composers[0]?.trim()) && leg) {
        trk.composers = [leg];
      }
      if ((!trk.lyricists || trk.lyricists.length === 0 || !trk.lyricists[0]?.trim()) && leg) {
        trk.lyricists = [leg];
      }
      if (!trk.genre && releaseData.genre) trk.genre = releaseData.genre;
      if (!trk.mood && releaseData.mood) trk.mood = releaseData.mood;
      return trk;
    });

    onUpdateReleaseData((prev) => ({ ...prev, tracks: syncedTracks }));
    setValidationErrors({});
    setIsValidationModalOpen(false);
    if (onStep3ViewChange) {
      onStep3ViewChange('track-details');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* -------------------------------------------------------------
     Progression 2: Track Details -> Cover Art (Step 4)
     ------------------------------------------------------------- */
  const handleValidateAndProceedToCoverArt = () => {
    const errors = {};
    const stageName = profile?.stage_name || profile?.name || '';
    const artistIdentities = [
      stageName,
      ...(releaseData.primaryArtists || []),
      ...(releaseData.featuringArtists || []),
    ].filter(Boolean);

    const tracksToCheck = releaseData.tracks || [];

    tracksToCheck.forEach((t, idx) => {
      if (!t.title?.trim()) {
        errors[`track_${idx}_title`] = `Track ${idx + 1}: Title is required.`;
      }

      if (!t.versionType) {
        errors[`track_${idx}_version`] = `Track ${idx + 1}: Version is required.`;
      }

      if (!t.mood) {
        errors[`track_${idx}_mood`] = `Track ${idx + 1}: Song Mood is required.`;
      }

      if (!t.language) {
        errors[`track_${idx}_lang`] = `Track ${idx + 1}: Language is required.`;
      }

      const comps = (t.composers || []).filter((c) => c && c.trim());
      if (comps.length === 0) {
        errors[`track_${idx}_composers`] = `Track ${idx + 1}: At least 1 composer (legal name) is required.`;
      } else {
        comps.forEach((c) => {
          const compCheck = validateLegalName(c, stageName, artistIdentities);
          if (!compCheck.isValid) {
            errors[`track_${idx}_comp_${c}`] = `Track ${idx + 1} Composer "${c}": ${compCheck.error}`;
          }
        });
      }

      if (t.vocalType !== 'Non-Vocal / Instrumental') {
        const lyrs = (t.lyricists || []).filter((l) => l && l.trim());
        if (lyrs.length === 0) {
          errors[`track_${idx}_lyricists`] = `Track ${idx + 1}: At least 1 lyricist (legal name) is required for vocal tracks.`;
        } else {
          lyrs.forEach((l) => {
            const lyrCheck = validateLegalName(l, stageName, artistIdentities);
            if (!lyrCheck.isValid) {
              errors[`track_${idx}_lyr_${l}`] = `Track ${idx + 1} Lyricist "${l}": ${lyrCheck.error}`;
            }
          });
        }
      }

      if (!t.mixEngineer?.trim()) {
        errors[`track_${idx}_mix`] = `Track ${idx + 1}: Mix Engineer is required.`;
      }

      if (!t.masterEngineer?.trim()) {
        errors[`track_${idx}_master`] = `Track ${idx + 1}: Master Engineer is required.`;
      }

      if (t.aiUsed && !t.aiDeclarationFile && !t.aiDeclarationFileName) {
        errors[`track_${idx}_ai`] = `Track ${idx + 1}: AI declaration PDF (≤ 5MB) is required.`;
      }

      if (t.hasSamples) {
        const smps = (t.samples || []).filter((s) => s.url && s.url.trim());
        if (smps.length === 0) {
          errors[`track_${idx}_samples`] = `Track ${idx + 1}: Sample link is required.`;
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setIsValidationModalOpen(true);
      return;
    }

    setValidationErrors({});
    setIsValidationModalOpen(false);
    onContinue();
  };

  const currentTrack = releaseData.tracks?.[selectedTrackIndex] || {
    title: '',
    versionType: 'Original',
    genre: 'Pop',
    mood: 'Happy / Joyful',
    language: 'Hindi',
    isrc: '',
    explicit: false,
    vocalType: 'Vocal',
    countryRecorded: 'India',
    recordingYear: new Date().getFullYear(),
    hasLyrics: false,
    lyrics: '',
    composers: [''],
    lyricists: [''],
    mixEngineer: '',
    masterEngineer: '',
    performers: [{ name: '', role: 'Vocalist' }],
    hasSamples: false,
    samples: [{ url: '', licenseUrl: '' }],
    aiUsed: false,
  };

  return (
    <div>
      {/* Grouped Validation Errors Modal */}
      <ValidationErrorsModal
        isOpen={isValidationModalOpen}
        onClose={() => setIsValidationModalOpen(false)}
        errors={validationErrors}
      />

      {/* =========================================================
          VIEW A: RELEASE INFORMATION SHEET (Matching 09.webp)
          ========================================================= */}
      {step3View === 'release-info' && (
        <div className="dist-card dist-rel-sheet-card" id="dist-release-info-sheet">
          <div className="dist-section-header" style={{ marginBottom: '1.25rem' }}>
            <h3 className="dist-section-title">
              <FileText size={20} color="#d9fb06" /> Release Information
            </h3>
            <p className="dist-section-subtitle">
              Core metadata for your {releaseData.releaseType?.toUpperCase() || 'SINGLE'}. This applies to the entire release across all DSPs.
            </p>
          </div>

          <div className="dist-rel-form-body">
            {/* Row 1: 2 columns (Release Title, Creator Legal Name) */}
            <div className="dist-rel-row-2col">
              <div className="dist-form-group">
                <label className="dist-label" htmlFor="rel-title">
                  Release Title <span className="dist-required-mark">*</span>
                </label>
                <input
                  id="rel-title"
                  type="text"
                  className={`dist-input ${validationErrors.title ? 'error' : ''}`}
                  placeholder="e.g. Midnight Serenade"
                  value={releaseData.title || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleReleaseChange('title', val);
                    if (validationErrors.title) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.title;
                        return next;
                      });
                    }
                  }}
                  maxLength={120}
                />
                <span className="dist-input-hint">
                  Trimmed title without promotional text (e.g., avoid "Official Audio" or emojis).
                </span>
              </div>

              <div className="dist-form-group">
                <label className="dist-label" htmlFor="rel-legal-name">
                  Creator Legal Name (Real Name) <span className="dist-required-mark">*</span>
                </label>
                <input
                  id="rel-legal-name"
                  type="text"
                  className={`dist-input ${validationErrors.legalName ? 'error' : ''}`}
                  placeholder="e.g. Rahul Sharma (as on official ID / PAN / Passport)"
                  value={releaseData.legalName || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleReleaseChange('legalName', val);
                    if (validationErrors.legalName) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.legalName;
                        return next;
                      });
                    }
                  }}
                  maxLength={120}
                />
                {validationErrors.legalName ? (
                  <span style={{ color: '#ff6b6b', fontSize: '0.8rem', marginTop: '0.3rem', display: 'block' }}>
                    {validationErrors.legalName}
                  </span>
                ) : (
                  <span className="dist-input-hint">
                    Must be your real official name for royalties, payouts, and copyright registration.
                  </span>
                )}
              </div>
            </div>

            {/* Row 2: 3 columns (Primary Genre, Release Mood, Release Date) */}
            <div className="dist-rel-row-3col">
              <div className="dist-form-group">
                <CustomSelect
                  label="Primary Genre"
                  required
                  options={GENRE_OPTIONS}
                  value={releaseData.genre || 'Pop'}
                  onChange={(val) => {
                    handleReleaseChange('genre', val);
                    if (validationErrors.genre) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.genre;
                        return next;
                      });
                    }
                  }}
                  placeholder="Select primary genre..."
                  searchable
                  error={validationErrors.genre}
                />
              </div>

              <div className="dist-form-group">
                <CustomSelect
                  label="Release Mood"
                  required
                  options={MOOD_OPTIONS}
                  value={releaseData.mood || 'Happy / Joyful'}
                  onChange={(val) => {
                    handleReleaseChange('mood', val);
                    if (validationErrors.mood) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.mood;
                        return next;
                      });
                    }
                  }}
                  placeholder="Select release mood..."
                  error={validationErrors.mood}
                />
              </div>

              <div className="dist-form-group">
                <DistributionDatePicker
                  label="Release Date"
                  required
                  value={releaseData.releaseDate || ''}
                  minDate={earliestDate}
                  onChange={(val) => {
                    handleReleaseChange('releaseDate', val);
                    if (validationErrors.releaseDate) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.releaseDate;
                        return next;
                      });
                    }
                  }}
                  error={validationErrors.releaseDate}
                />
              </div>
            </div>

            {/* Row 3: 3 columns (ISRC, C Line, P Line) */}
            <div className="dist-rel-row-3col">
              <div className="dist-form-group">
                <label className="dist-label" htmlFor="rel-isrc">
                  ISRC (if already assigned)
                </label>
                <input
                  id="rel-isrc"
                  type="text"
                  className={`dist-input ${validationErrors.isrc ? 'error' : ''}`}
                  placeholder="Enter ISRC if available (optional)"
                  value={releaseData.isrc || ''}
                  onChange={(e) => {
                    const val = e.target.value.toUpperCase();
                    handleReleaseChange('isrc', val);
                    if (validationErrors.isrc) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.isrc;
                        return next;
                      });
                    }
                  }}
                  maxLength={12}
                />
                <span className="dist-input-hint">
                  Leave blank if you do not have an existing ISRC.
                </span>
              </div>

              <div className="dist-form-group">
                <label className="dist-label" htmlFor="c-line">
                  C Line ( Copyrights Holder )
                </label>
                <input
                  id="c-line"
                  type="text"
                  className={`dist-input ${validationErrors.cLine ? 'error' : ''}`}
                  placeholder={`© ${new Date().getFullYear()} Artist Name`}
                  value={releaseData.cLine || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleReleaseChange('cLine', val);
                    if (validationErrors.cLine) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.cLine;
                        return next;
                      });
                    }
                  }}
                />
                <span className="dist-input-hint">
                  Required format: © YYYY Rights Holder (e.g. © {new Date().getFullYear()} Artist Name)
                </span>
              </div>

              <div className="dist-form-group">
                <label className="dist-label dist-p-line-label" htmlFor="p-line">
                  P Line (Publishing rights Holder)
                </label>
                <input
                  id="p-line"
                  type="text"
                  className={`dist-input ${validationErrors.pLine ? 'error' : ''}`}
                  placeholder={`℗ ${new Date().getFullYear()} SenritsuX / Artist Name`}
                  value={releaseData.pLine || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleReleaseChange('pLine', val);
                    if (validationErrors.pLine) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.pLine;
                        return next;
                      });
                    }
                  }}
                />
                <span className="dist-input-hint">
                  Required format: ℗ YYYY Rights Holder (e.g. ℗ {new Date().getFullYear()} SenritsuX)
                </span>
              </div>
            </div>

            {/* Row 4: Bottom Split */}
            <div className="dist-rel-bottom-split">
              <div className="dist-rel-bottom-left">
                <div className="dist-rel-artists-subrow">
                  <div className="dist-form-group">
                    <label className="dist-label">
                      <Users size={15} color="#d9fb06" /> Primary Artist(s) <span className="dist-required-mark">*</span>
                    </label>
                    {(releaseData.primaryArtists || ['']).map((artist, idx) => (
                      <div key={idx} className="dist-artist-input-wrap" style={{ marginBottom: idx > 0 ? '0.4rem' : '0' }}>
                        <input
                          type="text"
                          className="dist-input"
                          placeholder="e.g. Sj"
                          value={artist}
                          onChange={(e) => handleUpdatePrimaryArtist(idx, e.target.value)}
                        />
                        {idx === 0 ? (
                          <button
                            type="button"
                            className="dist-circular-add-btn"
                            onClick={handleAddPrimaryArtist}
                            title="Add Primary Artist"
                            aria-label="Add Primary Artist"
                          >
                            <Plus size={18} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="dist-remove-btn"
                            onClick={() => handleRemovePrimaryArtist(idx)}
                            title="Remove Primary Artist"
                            aria-label="Remove Primary Artist"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                    {validationErrors.primaryArtists && (
                      <span style={{ color: '#ff6b6b', fontSize: '0.8rem', marginTop: '0.2rem', display: 'block' }}>
                        {validationErrors.primaryArtists}
                      </span>
                    )}
                  </div>

                  <div className="dist-form-group">
                    <label className="dist-label">Featuring Artist(s)</label>
                    {(releaseData.featuringArtists && releaseData.featuringArtists.length > 0
                      ? releaseData.featuringArtists
                      : ['']
                    ).map((artist, idx) => (
                      <div key={idx} className="dist-artist-input-wrap" style={{ marginBottom: idx > 0 ? '0.4rem' : '0' }}>
                        <input
                          type="text"
                          className="dist-input"
                          placeholder="e.g. Featuring Artist"
                          value={artist}
                          onChange={(e) => {
                            if (!releaseData.featuringArtists || releaseData.featuringArtists.length === 0) {
                              handleReleaseChange('featuringArtists', [e.target.value]);
                            } else {
                              handleUpdateFeaturingArtist(idx, e.target.value);
                            }
                          }}
                        />
                        {idx === 0 ? (
                          <button
                            type="button"
                            className="dist-circular-add-btn"
                            onClick={handleAddFeaturingArtist}
                            title="Add Featuring Artist"
                            aria-label="Add Featuring Artist"
                          >
                            <Plus size={18} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="dist-remove-btn"
                            onClick={() => handleRemoveFeaturingArtist(idx)}
                            title="Remove Featuring Artist"
                            aria-label="Remove Featuring Artist"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <DistributionTimePicker
                  timeValue={releaseData.releaseTime || '00:00'}
                  timezoneValue={releaseData.releaseTimezone || 'Asia/Kolkata'}
                  onTimeChange={(val) => handleReleaseChange('releaseTime', val)}
                  onTimezoneChange={(val) => handleReleaseChange('releaseTimezone', val)}
                />
              </div>

              <div className="dist-rel-bottom-right">
                <div className="dist-social-row">
                  <div className="dist-social-brand">
                    <div className="dist-social-icon-box">
                      <InstagramIcon />
                    </div>
                    <span className="dist-social-name">IG Music</span>
                  </div>
                  <button
                    type="button"
                    className={`dist-social-toggle-btn ${releaseData.socialMonetisation?.instagramMusic !== false ? 'on' : 'off'}`}
                    onClick={() => handleSocialToggle('instagramMusic')}
                    aria-label="Toggle Instagram Music"
                  >
                    {releaseData.socialMonetisation?.instagramMusic !== false ? 'ON' : 'OFF'}
                  </button>
                </div>

                <div className="dist-social-row">
                  <div className="dist-social-brand">
                    <div className="dist-social-icon-box">
                      <YouTubeIcon />
                    </div>
                    <span className="dist-social-name">Content ID</span>
                  </div>
                  <button
                    type="button"
                    className={`dist-social-toggle-btn ${releaseData.socialMonetisation?.youtube !== false ? 'on' : 'off'}`}
                    onClick={() => handleSocialToggle('youtube')}
                    aria-label="Toggle YouTube Content ID"
                  >
                    {releaseData.socialMonetisation?.youtube !== false ? 'ON' : 'OFF'}
                  </button>
                </div>

                <div className="dist-social-row">
                  <div className="dist-social-brand">
                    <div className="dist-social-icon-box">
                      <TikTokIcon />
                    </div>
                    <span className="dist-social-name">Tik Tok</span>
                  </div>
                  <button
                    type="button"
                    className={`dist-social-toggle-btn ${releaseData.socialMonetisation?.tiktok !== false ? 'on' : 'off'}`}
                    onClick={() => handleSocialToggle('tiktok')}
                    aria-label="Toggle TikTok"
                  >
                    {releaseData.socialMonetisation?.tiktok !== false ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Release Information Footer Navigation */}
          <div className="dist-track-sheet-footer" style={{ marginTop: '2rem' }}>
            <button type="button" className="dist-btn-secondary" onClick={onBack}>
              <ArrowLeft size={18} /> Back
            </button>

            <button type="button" className="dist-btn-primary" onClick={handleProceedToTrackDetails}>
              Continue to Track Details <ArrowRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* =========================================================
          VIEW B: TRACK DETAILS SHEET (Matching 10.webp reference)
          ========================================================= */}
      {step3View === 'track-details' && (
        <div className="dist-card dist-track-sheet-card" id="dist-track-sheet">
          {/* Sheet Header: Music Icon + Title + Track Badge/Tabs */}
          <div className="dist-track-sheet-header">
            <div className="dist-track-sheet-title-group">
              <Music2 size={24} color="#d9fb06" className="dist-track-music-icon" />
              <h3 className="dist-track-sheet-title">
                Track Details ({totalTracks} Track{totalTracks > 1 ? 's' : ''})
              </h3>
            </div>

            <div className="dist-track-tabs-cluster" role="tablist">
              {Array.from({ length: totalTracks }).map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  role="tab"
                  aria-selected={selectedTrackIndex === idx}
                  className={`dist-track-tab-pill ${selectedTrackIndex === idx ? 'active' : ''}`}
                  onClick={() => setSelectedTrackIndex(idx)}
                >
                  Track {idx + 1}
                </button>
              ))}
            </div>
          </div>

          <div className="dist-track-sheet-body">
            {/* Row 1: 4 columns (Track Title *, Version *, Song Mood *, Language *) */}
            <div className="dist-track-row-4col">
              <div className="dist-form-group">
                <label className="dist-label" htmlFor={`trk-title-${selectedTrackIndex}`}>
                  Track Title <span className="dist-required-mark">*</span>
                </label>
                <input
                  id={`trk-title-${selectedTrackIndex}`}
                  type="text"
                  className="dist-input"
                  placeholder="eg. Midnight"
                  value={currentTrack.title || ''}
                  onChange={(e) => handleTrackChange(selectedTrackIndex, 'title', e.target.value)}
                  maxLength={120}
                />
              </div>

              <div className="dist-form-group">
                <CustomSelect
                  label="Version"
                  required
                  options={VERSION_OPTIONS}
                  value={currentTrack.versionType || 'Original'}
                  onChange={(val) => handleTrackChange(selectedTrackIndex, 'versionType', val)}
                  placeholder="Original"
                />
              </div>

              <div className="dist-form-group">
                <CustomSelect
                  label="Song Mood"
                  required
                  options={MOOD_OPTIONS}
                  value={currentTrack.mood || releaseData.mood || 'Happy / Joyful'}
                  onChange={(val) => handleTrackChange(selectedTrackIndex, 'mood', val)}
                  placeholder="Happy / Joyful"
                />
              </div>

              <div className="dist-form-group">
                <CustomSelect
                  label="Language"
                  required
                  options={LANGUAGE_OPTIONS}
                  value={currentTrack.language || 'Hindi'}
                  onChange={(val) => handleTrackChange(selectedTrackIndex, 'language', val)}
                  placeholder="Hindi"
                  searchable
                />
              </div>
            </div>

            {/* Row 2: Vocal / Instrumental Toggle + Explicit Content Toggle */}
            <div className="dist-track-row-toggles">
              <div className="dist-vocal-pill-group">
                <button
                  type="button"
                  className={`dist-vocal-pill-btn ${currentTrack.vocalType !== 'Non-Vocal / Instrumental' ? 'active' : ''}`}
                  onClick={() => handleTrackChange(selectedTrackIndex, 'vocalType', 'Vocal')}
                >
                  Vocal
                </button>
                <button
                  type="button"
                  className={`dist-vocal-pill-btn ${currentTrack.vocalType === 'Non-Vocal / Instrumental' ? 'active' : ''}`}
                  onClick={() => handleTrackChange(selectedTrackIndex, 'vocalType', 'Non-Vocal / Instrumental')}
                >
                  Instrumental
                </button>
              </div>

              <div className="dist-explicit-toggle-group">
                <span className="dist-explicit-label">Explicit Content</span>
                <div className="dist-yes-no-capsule">
                  <button
                    type="button"
                    className={`dist-yes-no-capsule-btn ${currentTrack.explicit ? 'active' : ''}`}
                    onClick={() => handleTrackChange(selectedTrackIndex, 'explicit', true)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={`dist-yes-no-capsule-btn ${!currentTrack.explicit ? 'active' : ''}`}
                    onClick={() => handleTrackChange(selectedTrackIndex, 'explicit', false)}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>

            {/* Row 3: 2 columns (Composer(s) * ( Legal Name ), Lyricist(s) * ( Legal Name )) */}
            <div className="dist-track-row-2col">
              <div className="dist-form-group">
                <label className="dist-label">
                  Composer(s) <span className="dist-required-mark">*</span> ( Legal Name )
                </label>
                {(currentTrack.composers || ['']).map((comp, cIdx) => (
                  <div key={cIdx} className="dist-credit-input-wrap">
                    <input
                      type="text"
                      className="dist-input"
                      placeholder="Legal Name Only"
                      value={comp}
                      onChange={(e) => handleUpdateComposer(selectedTrackIndex, cIdx, e.target.value)}
                    />
                    {cIdx === 0 ? (
                      <button
                        type="button"
                        className="dist-circular-add-btn"
                        onClick={() => handleAddComposer(selectedTrackIndex)}
                        title="Add Composer"
                        aria-label="Add Composer"
                      >
                        <Plus size={18} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="dist-remove-btn"
                        onClick={() => handleRemoveComposer(selectedTrackIndex, cIdx)}
                        title="Remove Composer"
                        aria-label="Remove Composer"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="dist-form-group">
                <label className="dist-label">
                  Lyricist(s) <span className="dist-required-mark">*</span> ( Legal Name )
                </label>
                {(currentTrack.lyricists || ['']).map((lyr, lIdx) => (
                  <div key={lIdx} className="dist-credit-input-wrap">
                    <input
                      type="text"
                      className="dist-input"
                      placeholder="Legal Name Only"
                      value={lyr}
                      disabled={currentTrack.vocalType === 'Non-Vocal / Instrumental'}
                      onChange={(e) => handleUpdateLyricist(selectedTrackIndex, lIdx, e.target.value)}
                    />
                    {lIdx === 0 ? (
                      <button
                        type="button"
                        className="dist-circular-add-btn"
                        onClick={() => handleAddLyricist(selectedTrackIndex)}
                        disabled={currentTrack.vocalType === 'Non-Vocal / Instrumental'}
                        title="Add Lyricist"
                        aria-label="Add Lyricist"
                      >
                        <Plus size={18} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="dist-remove-btn"
                        onClick={() => handleRemoveLyricist(selectedTrackIndex, lIdx)}
                        title="Remove Lyricist"
                        aria-label="Remove Lyricist"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Row 4: 4 columns (Mix Engineer *, Master Engineer *, Recording Country, Recording Year) */}
            <div className="dist-track-row-4col">
              <div className="dist-form-group">
                <label className="dist-label" htmlFor={`mix-eng-${selectedTrackIndex}`}>
                  Mix Engineer <span className="dist-required-mark">*</span>
                </label>
                <input
                  id={`mix-eng-${selectedTrackIndex}`}
                  type="text"
                  className="dist-input"
                  placeholder="Mix Engineer Name"
                  value={currentTrack.mixEngineer || ''}
                  onChange={(e) => handleTrackChange(selectedTrackIndex, 'mixEngineer', e.target.value)}
                />
              </div>

              <div className="dist-form-group">
                <label className="dist-label" htmlFor={`master-eng-${selectedTrackIndex}`}>
                  Master Engineer <span className="dist-required-mark">*</span>
                </label>
                <input
                  id={`master-eng-${selectedTrackIndex}`}
                  type="text"
                  className="dist-input"
                  placeholder="Master Engineer Name"
                  value={currentTrack.masterEngineer || ''}
                  onChange={(e) => handleTrackChange(selectedTrackIndex, 'masterEngineer', e.target.value)}
                />
              </div>

              <div className="dist-form-group">
                <CustomSelect
                  label="Recording Country"
                  options={COUNTRIES_LIST}
                  value={currentTrack.countryRecorded || 'India'}
                  onChange={(val) => handleTrackChange(selectedTrackIndex, 'countryRecorded', val)}
                  placeholder="India"
                  searchable
                />
              </div>

              <div className="dist-form-group">
                <label className="dist-label" htmlFor={`rec-year-${selectedTrackIndex}`}>
                  Recording Year
                </label>
                <input
                  id={`rec-year-${selectedTrackIndex}`}
                  type="number"
                  className="dist-input"
                  placeholder="YYYY"
                  value={currentTrack.recordingYear || new Date().getFullYear()}
                  onChange={(e) =>
                    handleTrackChange(
                      selectedTrackIndex,
                      'recordingYear',
                      parseInt(e.target.value, 10) || new Date().getFullYear()
                    )
                  }
                  min={1900}
                  max={new Date().getFullYear() + 1}
                />
              </div>
            </div>

            {/* Row 5: Performer Credits (Musicians / Vocalist) * */}
            <div className="dist-performer-section">
              <div className="dist-performer-header">
                <label className="dist-label" style={{ margin: 0 }}>
                  Performer Credits ( Musicians / Vocalist ) <span className="dist-required-mark">*</span>
                </label>
                <button
                  type="button"
                  className="dist-circular-add-btn"
                  onClick={() => handleAddPerformer(selectedTrackIndex)}
                  title="Add Performer"
                  aria-label="Add Performer"
                >
                  <Plus size={16} />
                </button>
              </div>

              {(currentTrack.performers || [{ name: '', role: 'Vocalist' }]).map((perf, pIdx) => (
                <div key={pIdx} className="dist-performer-row">
                  <div className="dist-performer-role-select">
                    <CustomSelect
                      options={PERFORMER_ROLES}
                      value={perf.role || 'Vocalist'}
                      onChange={(val) => handleUpdatePerformer(selectedTrackIndex, pIdx, 'role', val)}
                      placeholder="Role"
                      searchable
                    />
                  </div>
                  <input
                    type="text"
                    className="dist-input dist-performer-name-input"
                    placeholder="Performer Name"
                    value={perf.name || ''}
                    onChange={(e) => handleUpdatePerformer(selectedTrackIndex, pIdx, 'name', e.target.value)}
                  />
                  <button
                    type="button"
                    className="dist-performer-trash-btn"
                    onClick={() => handleRemovePerformer(selectedTrackIndex, pIdx)}
                    title="Remove Performer"
                    aria-label="Remove Performer"
                  >
                    <Trash2 size={16} color="#ff5252" />
                  </button>
                </div>
              ))}
            </div>

            {/* Row 6: 3 Toggles (Lyrics, Used AI In Track, Contain Samples) */}
            <div className="dist-track-row-3toggles">
              <div className="dist-toggle-item">
                <span className="dist-toggle-label">Lyrics</span>
                <div className="dist-yes-no-capsule">
                  <button
                    type="button"
                    className={`dist-yes-no-capsule-btn ${currentTrack.hasLyrics ? 'active' : ''}`}
                    onClick={() => handleTrackChange(selectedTrackIndex, 'hasLyrics', true)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={`dist-yes-no-capsule-btn ${!currentTrack.hasLyrics ? 'active' : ''}`}
                    onClick={() => handleTrackChange(selectedTrackIndex, 'hasLyrics', false)}
                  >
                    No
                  </button>
                </div>
              </div>

              <div className="dist-toggle-item">
                <span className="dist-toggle-label">Used AI In Track</span>
                <div className="dist-yes-no-capsule">
                  <button
                    type="button"
                    className={`dist-yes-no-capsule-btn ${currentTrack.aiUsed ? 'active' : ''}`}
                    onClick={() => handleTrackChange(selectedTrackIndex, 'aiUsed', true)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={`dist-yes-no-capsule-btn ${!currentTrack.aiUsed ? 'active' : ''}`}
                    onClick={() => handleTrackChange(selectedTrackIndex, 'aiUsed', false)}
                  >
                    No
                  </button>
                </div>
              </div>

              <div className="dist-toggle-item">
                <span className="dist-toggle-label">Contain Samples</span>
                <div className="dist-yes-no-capsule">
                  <button
                    type="button"
                    className={`dist-yes-no-capsule-btn ${currentTrack.hasSamples ? 'active' : ''}`}
                    onClick={() => handleTrackChange(selectedTrackIndex, 'hasSamples', true)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={`dist-yes-no-capsule-btn ${!currentTrack.hasSamples ? 'active' : ''}`}
                    onClick={() => handleTrackChange(selectedTrackIndex, 'hasSamples', false)}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>

            {/* Optional Drawers when toggles are active */}
            {currentTrack.hasLyrics && (
              <div className="dist-lyrics-drawer">
                <label className="dist-label">Song Lyrics</label>
                <textarea
                  className="dist-textarea"
                  rows={4}
                  placeholder="Enter full song lyrics line-by-line..."
                  value={currentTrack.lyrics || ''}
                  onChange={(e) => handleTrackChange(selectedTrackIndex, 'lyrics', e.target.value)}
                />
              </div>
            )}

            {currentTrack.aiUsed && (
              <div className="dist-ai-drawer">
                <label className="dist-label">
                  Upload AI Usage License / Declaration Document <span className="dist-required-mark">*</span> (PDF only, Max 5 MB)
                </label>
                {currentTrack.aiDeclarationFileName ? (
                  <div className="dist-ai-file-uploaded">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FileCheck size={18} />
                      <span>{currentTrack.aiDeclarationFileName}</span>
                    </div>
                    <button
                      type="button"
                      className="dist-remove-btn"
                      onClick={() => {
                        handleTrackChange(selectedTrackIndex, 'aiDeclarationFile', null);
                        handleTrackChange(selectedTrackIndex, 'aiDeclarationFileName', null);
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : (
                  <div
                    className="dist-ai-upload-box"
                    onClick={() => document.getElementById(`ai-pdf-input-${selectedTrackIndex}`)?.click()}
                  >
                    <input
                      id={`ai-pdf-input-${selectedTrackIndex}`}
                      type="file"
                      accept="application/pdf,.pdf"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        if (e.target.files?.[0]) handleAiPdfUpload(selectedTrackIndex, e.target.files[0]);
                      }}
                    />
                    <UploadCloud size={24} color="#d9fb06" />
                    <span>Click to Upload AI Declaration PDF (PDF only, ≤ 5 MB)</span>
                  </div>
                )}
              </div>
            )}

            {currentTrack.hasSamples && (
              <div className="dist-samples-drawer">
                <label className="dist-label">Sample References</label>
                {(currentTrack.samples || [{ url: '', licenseUrl: '' }]).map((sample, sIdx) => (
                  <div key={sIdx} className="dist-credit-input-wrap">
                    <input
                      type="url"
                      className="dist-input"
                      placeholder="Sampled Track Link / Reference"
                      value={sample.url || ''}
                      onChange={(e) => handleUpdateSample(selectedTrackIndex, sIdx, 'url', e.target.value)}
                    />
                    <input
                      type="url"
                      className="dist-input"
                      placeholder="Sample Clearance / License Link (optional)"
                      value={sample.licenseUrl || ''}
                      onChange={(e) => handleUpdateSample(selectedTrackIndex, sIdx, 'licenseUrl', e.target.value)}
                    />
                    {sIdx > 0 && (
                      <button
                        type="button"
                        className="dist-remove-btn"
                        onClick={() => handleRemoveSample(selectedTrackIndex, sIdx)}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="dist-add-btn"
                  onClick={() => handleAddSample(selectedTrackIndex)}
                  style={{ marginTop: '0.4rem' }}
                >
                  <Plus size={14} /> Add Another Sample Reference
                </button>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="dist-track-sheet-footer">
            <button
              type="button"
              className="dist-btn-secondary"
              onClick={() => onStep3ViewChange && onStep3ViewChange('release-info')}
            >
              <ArrowLeft size={18} /> Back
            </button>

            <button
              type="button"
              className="dist-btn-primary"
              onClick={handleValidateAndProceedToCoverArt}
            >
              Continue to Cover Art <ArrowRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
